import { useEffect, useRef, useState } from "react";
import "./index.css";
import Field from "./Field";
import { CloseIcon, PlusIcon } from "../../icons";
import { newSlotId } from "../../sync/doc";

/** A switch on the sheet. */
function Switch({ checked, onChange, children, disabled }) {
  return (
    <label className={`Switch${disabled ? " is-disabled" : ""}`}>
      <span className="Switch-label">{children}</span>
      <input
        type="checkbox"
        role="switch"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="Switch-track" aria-hidden="true">
        <span className="Switch-knob" />
      </span>
    </label>
  );
}

// idle and synced share this wording, and both get the account address appended
// when it is known. Not fetched yet, or the lookup failed: fall back to the
// plain sentence — the label must never claim more than it knows.
const CONNECTED_MESSAGE = (account) =>
  account ? `Connected to Google Drive as ${account}.` : "Connected to Google Drive.";

const SYNC_MESSAGE = {
  idle: CONNECTED_MESSAGE,
  syncing: () => "Syncing…",
  synced: CONNECTED_MESSAGE,
  offline: () => "Offline — your ticks are saved here and will sync later.",
  error: () => "Couldn't reach Drive. Your ticks are safe here; it will try again.",
  reconnect: () => "Sync is paused. Your ticks are safe here — reconnect to resume.",
};

// The most a strip can hold. The printed sheet is the binding limit: it deals
// every column out of the page's width, and each slot column must stay wider
// than its checkbox. 16 was measured to fit on A4 with the long weekday;
// measure again before raising it (see `.Paper-box` in Paper/index.css).
const MAX_COLUMNS = 16;

/** The settings sheet. */
function Settings(props) {
  const { open, onClose } = props;
  const dialogRef = useRef(null);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    if (!open) setConfirmReset(false);
  }, [open]);

  const onBackdropClick = (event) => {
    if (event.target === dialogRef.current) onClose();
  };

  const onDeleteField = (index) => {
    const slots = [...props.slots];
    slots.splice(index, 1);
    props.onSetSlots(slots);
  };
  const onUpdateField = (index, value) => {
    const slots = [...props.slots];
    slots[index] = { ...slots[index], name: value };
    props.onSetSlots(slots);
  };
  const onMoveUpField = (index) => {
    if (index === 0) return;
    const slots = [...props.slots];
    [slots[index - 1], slots[index]] = [slots[index], slots[index - 1]];
    props.onSetSlots(slots);
  };
  const onMoveDownField = (index) => {
    if (index === props.slots.length - 1) return;
    const slots = [...props.slots];
    [slots[index + 1], slots[index]] = [slots[index], slots[index + 1]];
    props.onSetSlots(slots);
  };
  const onAddField = () => props.onSetSlots([...props.slots, { id: newSlotId(props.slots, props.ticks), name: "New task" }]);

  const onReset = () => {
    if (!confirmReset) {
      setConfirmReset(true);
      return;
    }
    props.onReset();
    setConfirmReset(false);
  };

  return (
    <dialog
      className="Sheet screen-only"
      ref={dialogRef}
      onClose={onClose}
      onCancel={onClose}
      onClick={onBackdropClick}
      aria-labelledby="settings-title"
    >
      <form
        method="dialog"
        className="Sheet-form"
        onSubmit={(e) => {
          e.preventDefault();
          onClose();
        }}
      >
        <header className="Sheet-head">
          <h2 id="settings-title" className="Sheet-title">
            Settings
          </h2>
          <button type="button" className="IconButton" onClick={onClose} aria-label="Close settings" title="Close">
            <CloseIcon />
          </button>
        </header>

        <div className="Sheet-body">
          <section className="Sheet-section">
            <label className="Sheet-row">
              <span className="Sheet-label">Title</span>
              <span className="Sheet-inputwrap">
                <input
                  className="Sheet-input"
                  value={props.title}
                  maxLength={32}
                  onChange={(e) => props.onSetTitle(e.target.value)}
                  aria-describedby="title-count"
                />
                <span className="Sheet-count" id="title-count">
                  {props.title.length} / 32
                </span>
              </span>
            </label>
          </section>

          <section className="Sheet-section">
            <div className="Sheet-label">Columns</div>
            <div className="Sheet-fields">
              {props.slots.map((slot, index) => (
                <Field
                  key={slot.id}
                  index={index}
                  count={props.slots.length}
                  label={slot.name}
                  onDelete={() => onDeleteField(index)}
                  onUpdate={(value) => onUpdateField(index, value)}
                  onMoveUp={() => onMoveUpField(index)}
                  onMoveDown={() => onMoveDownField(index)}
                />
              ))}
            </div>
            <button
              type="button"
              className="PaperButton"
              onClick={onAddField}
              disabled={props.slots.length >= MAX_COLUMNS}
            >
              <PlusIcon width="16" height="16" /> Add column
            </button>
            {props.slots.length >= MAX_COLUMNS ? (
              <p className="Sheet-note">Sixteen columns is the most a strip can hold.</p>
            ) : null}
          </section>

          <section className="Sheet-section">
            <div className="Sheet-label">Custom recurrence</div>
            <div className="Sheet-inline">
              <span>Repeat every</span>
              <input
                className="Sheet-input Sheet-number"
                type="number"
                inputMode="numeric"
                min={1}
                max={10}
                value={props.repeatStep}
                onChange={(e) => {
                  const n = Math.min(10, Math.max(1, parseInt(e.target.value || "1", 10) || 1));
                  props.onSetRepeatStep(n);
                }}
                aria-label="Repeat every"
              />
              <select
                className="Sheet-select"
                value={props.repeatUnit}
                onChange={(e) => props.onSetRepeatUnit(e.target.value)}
                aria-label="Repeat unit"
              >
                <option value="day">day</option>
                <option value="week">week</option>
              </select>
            </div>
          </section>

          <section className="Sheet-section">
            <div className="Sheet-label">Display</div>
            <div className="Sheet-checks">
              <Switch checked={props.showWeekdays} onChange={props.onSetShowWeekdays}>
                Show weekday
              </Switch>
              <Switch checked={props.shouldPrintHeader} onChange={props.onSetShouldPrintHeader}>
                Print the title as a header
              </Switch>
            </div>
            <div className="Sheet-formats">
              <label className="Sheet-field">
                <span className="Sheet-fieldlabel">Date format</span>
                <select
                  className="Sheet-select"
                  value={props.dateFormat}
                  onChange={(e) => props.onSetDateFormat(e.target.value)}
                >
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  <option value="MM-DD">MM-DD</option>
                  <option value="DD">DD</option>
                  <option value="MMM">MMM</option>
                </select>
              </label>
              <label className="Sheet-field">
                <span className="Sheet-fieldlabel">Weekday format</span>
                <select
                  className="Sheet-select"
                  value={props.weekdayFormat}
                  disabled={!props.showWeekdays}
                  onChange={(e) => props.onSetWeekdayFormat(e.target.value)}
                >
                  <option value="dddd">dddd</option>
                  <option value="ddd">ddd</option>
                </select>
              </label>
            </div>
          </section>

          {props.sync ? (
            <section className="Sheet-section">
              <div className="Sheet-label">Sync</div>
              {props.sync.enabled ? (
                <div className="Sheet-sync">
                  <p className="Sheet-note">
                    {(SYNC_MESSAGE[props.sync.status] || SYNC_MESSAGE.idle)(props.sync.account)}
                  </p>
                  {props.sync.status === "reconnect" && props.sync.reason ? (
                    <p className="Sheet-note">{props.sync.reason}</p>
                  ) : null}
                  {props.sync.status === "reconnect" ? (
                    <button type="button" className="PaperButton" onClick={props.sync.onConnect}>
                      Reconnect
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="PaperButton"
                      onClick={props.sync.onSyncNow}
                      disabled={props.sync.status === "syncing"}
                    >
                      Sync now
                    </button>
                  )}
                  <button type="button" className="PaperButton" onClick={props.sync.onDisconnect}>
                    Disconnect
                  </button>
                </div>
              ) : (
                <div className="Sheet-sync">
                  <p className="Sheet-note">
                    Keeps a copy of your schedule in your own Google Drive, so your devices match. Nothing is sent to
                    us, and you can turn it off at any time.
                  </p>
                  <button type="button" className="PaperButton" onClick={props.sync.onConnect}>
                    Connect Google Drive
                  </button>
                </div>
              )}
            </section>
          ) : null}

          <footer className="Sheet-foot">
            <span className="Sheet-version">Version {process.env.REACT_APP_VERSION}</span>
            <div className="Sheet-reset">
              {confirmReset ? (
                <span className="Sheet-note">
                  {props.sync && props.sync.enabled
                    ? "Clears the ticks and settings on this device, and on your other devices when they next sync."
                    : "Clears every tick and setting."}
                </span>
              ) : null}
              <button
                type="button"
                className={`PaperButton${confirmReset ? " is-danger" : ""}`}
                onClick={onReset}
                onBlur={() => setConfirmReset(false)}
              >
                {confirmReset ? "Yes, reset everything" : "Reset to defaults"}
              </button>
            </div>
          </footer>
        </div>
      </form>
    </dialog>
  );
}

export default Settings;
